import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import {
  UserPlus,
  X,
  Mail,
  Phone,
  Calendar,
  User,
  ArrowRight,
  CheckCircle2,
  XCircle,
  MessageSquare,
  FileText,
  Search,
  Plus,
  LayoutGrid,
  List,
  Clock,
  Save,
  Loader2,
  AlertCircle,
  ChevronRight,
  ExternalLink,
  Tag,
  Edit3,
  Check,
} from "lucide-react";
import { intakeApi, usersApi, conversationsApi } from "../../services/api";
import SearchableSelect, { SelectOption } from "../ui/SearchableSelect";
import PremiumModal from "../ui/PremiumModal";
import ConvertLeadWizard from "./ConvertLeadWizard";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Stage {
  key: string;
  label: string;
  color: string;
  order: number;
}

interface Submission {
  _id: string;
  formId: { _id: string; name: string } | null;
  data: Record<string, any>;
  status: string;
  stage: string;
  assignedTo: { _id: string; name: string; email: string } | null;
  convertedToClientId: { _id: string; displayName: string } | null;
  convertedToMatterId: { _id: string; title: string; matterNumber: string } | null;
  notes: string | null;
  dueDate: string | null;
  submittedAt: string;
  createdAt: string;
}

interface TeamUser {
  _id: string;
  name: string;
  email: string;
}

interface Conversation {
  _id: string;
  subject?: string;
  lastMessageAt?: string;
  lastMessageBody?: string;
  messageCount?: number;
}

interface Toast {
  id: string;
  message: string;
  type: "success" | "error" | "info";
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PRIMARY = "var(--color-primary)";
const HOVER = "var(--color-primary-hover)";
const ACCENT = "var(--color-accent)";

const REFERRAL_SOURCES: SelectOption[] = [
  { value: "website", label: "Website" },
  { value: "referral", label: "Referral" },
  { value: "walk_in", label: "Walk-in" },
  { value: "phone_call", label: "Phone Call" },
  { value: "social_media", label: "Social Media" },
  { value: "advertisement", label: "Advertisement" },
  { value: "other", label: "Other" },
];

const PRACTICE_AREAS: SelectOption[] = [
  { value: "real_estate", label: "Real Estate" },
  { value: "family", label: "Family" },
  { value: "litigation", label: "Litigation" },
  { value: "criminal", label: "Criminal" },
  { value: "corporate", label: "Corporate" },
  { value: "immigration", label: "Immigration" },
  { value: "wills_estates", label: "Wills & Estates" },
  { value: "other", label: "Other" },
];

// Stage-key to tailwind color mapping fallback
const STAGE_COLOR_MAP: Record<string, { bg: string; text: string; border: string; badge: string }> = {
  new: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-300", badge: "bg-blue-100 text-blue-700" },
  contacted: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-300", badge: "bg-amber-100 text-amber-700" },
  qualified: { bg: "bg-purple-50", text: "text-purple-700", border: "border-purple-300", badge: "bg-purple-100 text-purple-700" },
  consultation: { bg: "bg-cyan-50", text: "text-cyan-700", border: "border-cyan-300", badge: "bg-cyan-100 text-cyan-700" },
  proposal: { bg: "bg-indigo-50", text: "text-indigo-700", border: "border-indigo-300", badge: "bg-indigo-100 text-indigo-700" },
  converted: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-300", badge: "bg-emerald-100 text-emerald-700" },
  lost: { bg: "bg-red-50", text: "text-red-700", border: "border-red-300", badge: "bg-red-100 text-red-700" },
  rejected: { bg: "bg-red-50", text: "text-red-700", border: "border-red-300", badge: "bg-red-100 text-red-700" },
};

const DEFAULT_STAGE_COLORS = { bg: "bg-slate-50", text: "text-slate-700", border: "border-slate-300", badge: "bg-slate-100 text-slate-700" };

function getStageColors(key: string) {
  return STAGE_COLOR_MAP[key] || DEFAULT_STAGE_COLORS;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function extractContactInfo(data: Record<string, any> | undefined | null) {
  if (!data) return { name: "Unknown", email: null, phone: null };
  const name =
    data.name ||
    data.full_name ||
    data.fullName ||
    [data.first_name || data.firstName, data.last_name || data.lastName]
      .filter(Boolean)
      .join(" ") ||
    "Unknown";
  const email = data.email || data.contact_email || null;
  const phone = data.phone || data.phone_number || data.contact_phone || null;
  return { name, email, phone };
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("");
}

function formatDate(dateStr: string, includeYear = false): string {
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" };
  if (includeYear) opts.year = "numeric";
  return new Date(dateStr).toLocaleDateString("en-US", opts);
}

function formatRelativeTime(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateStr);
}

function getSourceLabel(source: string | undefined): string | null {
  if (!source) return null;
  const found = REFERRAL_SOURCES.find((s) => s.value === source);
  return found ? found.label : source;
}

function isDueSoon(dueDate: string | null): boolean {
  if (!dueDate) return false;
  const due = new Date(dueDate);
  const now = new Date();
  const diffDays = (due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
  return diffDays >= 0 && diffDays <= 2;
}

function isOverdue(dueDate: string | null): boolean {
  if (!dueDate) return false;
  return new Date(dueDate) < new Date();
}

let toastIdCounter = 0;

// ─── Toast System ─────────────────────────────────────────────────────────────

const ToastContainer: React.FC<{ toasts: Toast[]; onDismiss: (id: string) => void }> = ({
  toasts,
  onDismiss,
}) => (
  <div className="fixed bottom-6 right-6 z-[100] flex flex-col gap-2">
    <AnimatePresence>
      {toasts.map((toast) => (
        <motion.div
          key={toast.id}
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 10, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className={`flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium ${
            toast.type === "error"
              ? "bg-red-50 border-red-200 text-red-700"
              : toast.type === "success"
              ? "bg-emerald-50 border-emerald-200 text-emerald-700"
              : "bg-blue-50 border-blue-200 text-blue-700"
          }`}
        >
          {toast.type === "error" ? (
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
          ) : toast.type === "success" ? (
            <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
          ) : null}
          <span>{toast.message}</span>
          <button
            onClick={() => onDismiss(toast.id)}
            className="ml-2 p-0.5 rounded hover:bg-black/5 transition-colors"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </motion.div>
      ))}
    </AnimatePresence>
  </div>
);

// ─── Lead Card ────────────────────────────────────────────────────────────────

interface LeadCardProps {
  sub: Submission;
  index: number;
  stage: Stage;
  conversationCounts: Record<string, number>;
  onClick: () => void;
}

const LeadCard: React.FC<LeadCardProps> = React.memo(
  ({ sub, index, stage, conversationCounts, onClick }) => {
    const contact = extractContactInfo(sub.data);
    const sourceLabel = getSourceLabel(sub.data.referral_source || sub.data.source);
    const msgCount = conversationCounts[sub._id] || 0;
    const dueSoon = isDueSoon(sub.dueDate);
    const overdue = isOverdue(sub.dueDate);

    return (
      <Draggable draggableId={sub._id} index={index}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className={`bg-white rounded-xl border cursor-grab active:cursor-grabbing group ${
              snapshot.isDragging
                ? "shadow-2xl border-brand-light ring-2 ring-brand-light scale-[1.03] rotate-[1.5deg]"
                : "border-slate-200 shadow-sm hover:shadow-md hover:border-slate-300"
            }`}
            style={{
              ...provided.draggableProps.style,
              transition: snapshot.isDragging
                ? provided.draggableProps.style?.transition
                : `all 0.2s cubic-bezier(0.2, 0, 0, 1), ${provided.draggableProps.style?.transition || ""}`,
            }}
            onClick={onClick}
          >
            <div className="p-3.5">
              {/* Name row */}
              <div className="flex items-start gap-2 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div
                      className="h-7 w-7 rounded-full flex items-center justify-center text-white font-semibold text-[10px] flex-shrink-0"
                      style={{ backgroundColor: ACCENT }}
                    >
                      {getInitials(contact.name)}
                    </div>
                    <h4 className="text-sm font-semibold text-slate-800 truncate">
                      {contact.name}
                    </h4>
                  </div>
                </div>
              </div>

              {/* Contact info */}
              {contact.email && (
                <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1 ml-6">
                  <Mail className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">{contact.email}</span>
                </div>
              )}
              {contact.phone && (
                <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-1 ml-6">
                  <Phone className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate">{contact.phone}</span>
                </div>
              )}

              {/* Footer row: date, source badge, messages, assignee */}
              <div className="flex items-center justify-between mt-2.5 pt-2.5 border-t border-slate-100">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {/* Due date / submitted date */}
                  {sub.dueDate ? (
                    <div
                      className={`flex items-center gap-1 text-xs font-medium ${
                        overdue
                          ? "text-red-600"
                          : dueSoon
                          ? "text-amber-600"
                          : "text-slate-400"
                      }`}
                    >
                      <Clock className="h-3 w-3" />
                      {formatDate(sub.dueDate)}
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 text-xs text-slate-400">
                      <Calendar className="h-3 w-3" />
                      {formatDate(sub.submittedAt)}
                    </div>
                  )}

                  {/* Source badge */}
                  {sourceLabel && (
                    <span className="text-[10px] font-medium bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-md truncate max-w-[80px]">
                      {sourceLabel}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  {/* Message count */}
                  {msgCount > 0 && (
                    <div className="flex items-center gap-1 text-xs text-slate-400">
                      <MessageSquare className="h-3 w-3" />
                      <span>{msgCount}</span>
                    </div>
                  )}

                  {/* Assignee avatar */}
                  {sub.assignedTo && (
                    <div
                      className="h-5 w-5 rounded-full flex items-center justify-center text-white font-semibold text-[8px] flex-shrink-0"
                      style={{ backgroundColor: PRIMARY }}
                      title={sub.assignedTo.name}
                    >
                      {getInitials(sub.assignedTo.name)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </Draggable>
    );
  }
);

LeadCard.displayName = "LeadCard";

// ─── Board Column ─────────────────────────────────────────────────────────────

interface ColumnProps {
  stage: Stage;
  submissions: Submission[];
  conversationCounts: Record<string, number>;
  onCardClick: (sub: Submission) => void;
  onAddLead: () => void;
  isFirstColumn?: boolean;
}

const BoardColumn: React.FC<ColumnProps> = React.memo(
  ({ stage, submissions, conversationCounts, onCardClick, onAddLead, isFirstColumn }) => {
    const colors = getStageColors(stage.key);

    return (
      <div className="flex-shrink-0 w-[300px] flex flex-col max-h-full">
        {/* Column header */}
        <div
          className={`rounded-t-xl px-4 py-3 border-b-2 ${colors.border} bg-white flex-shrink-0`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {stage.color && (
                <div
                  className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: stage.color }}
                />
              )}
              <h3 className={`text-sm font-semibold ${colors.text}`}>
                {stage.label}
              </h3>
            </div>
            <div className="flex items-center gap-1.5">
              <span
                className={`text-xs font-bold px-2 py-0.5 rounded-full ${colors.badge}`}
              >
                {submissions.length}
              </span>
              <button
                  onClick={onAddLead}
                  className="p-1 rounded-lg text-slate-400 hover:text-brand-accent hover:bg-brand-light transition-colors"
                  title="Add lead"
                >
                  <Plus className="h-4 w-4" />
                </button>
            </div>
          </div>
        </div>

        {/* Droppable area */}
        <Droppable droppableId={stage.key}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`flex-1 rounded-b-xl p-2 min-h-[120px] space-y-2 overflow-y-auto transition-colors duration-200 ${
                snapshot.isDraggingOver
                  ? "bg-brand-light/70 ring-2 ring-inset ring-brand-light/50"
                  : "bg-slate-50/80"
              }`}
              style={{ maxHeight: "calc(100vh - 220px)" }}
            >
              {submissions.map((sub, index) => (
                <LeadCard
                  key={sub._id}
                  sub={sub}
                  index={index}
                  stage={stage}
                  conversationCounts={conversationCounts}
                  onClick={() => onCardClick(sub)}
                />
              ))}
              {provided.placeholder}
              {submissions.length === 0 && !snapshot.isDraggingOver && (
                <div className="flex flex-col items-center justify-center py-6 text-slate-300 text-center">
                  <FileText className="h-6 w-6 mb-1 mx-auto" />
                  <span className="text-xs">Drop leads here</span>
                </div>
              )}
              {!snapshot.isDraggingOver && (
                <button
                  onClick={onAddLead}
                  className="flex items-center justify-center gap-1.5 w-full py-2 mt-1 rounded-lg border border-dashed border-slate-200 hover:border-brand-light hover:bg-brand-light/50 transition-all text-slate-400 hover:text-brand-accent text-xs font-medium"
                >
                  <Plus className="h-3.5 w-3.5" />
                  Add Lead
                </button>
              )}
            </div>
          )}
        </Droppable>
      </div>
    );
  }
);

BoardColumn.displayName = "BoardColumn";

// ─── Detail Drawer ────────────────────────────────────────────────────────────

interface DrawerProps {
  open: boolean;
  submission: Submission | null;
  stages: Stage[];
  teamUsers: TeamUser[];
  onClose: () => void;
  onUpdate: (id: string, data: Record<string, any>) => Promise<void>;
  onConvertClick: () => void;
  onMarkLost: () => void;
  conversations: Conversation[];
  loadingConversations: boolean;
}

const DetailDrawer: React.FC<DrawerProps> = ({
  open,
  submission,
  stages,
  teamUsers,
  onClose,
  onUpdate,
  onConvertClick,
  onMarkLost,
  conversations,
  loadingConversations,
}) => {
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState("");
  const [notesValue, setNotesValue] = useState("");
  const [saving, setSaving] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const notesTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const contact = submission ? extractContactInfo(submission.data) : null;
  const currentStage = submission
    ? stages.find((s) => s.key === submission.stage) || null
    : null;

  // Sync local state when submission changes
  useEffect(() => {
    if (submission) {
      setNameValue(extractContactInfo(submission.data).name);
      setNotesValue(submission.notes || "");
      setEditingName(false);
    }
  }, [submission?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (editingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [editingName]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (notesTimeoutRef.current) clearTimeout(notesTimeoutRef.current);
    };
  }, []);

  const handleNameSave = async () => {
    if (!submission || !nameValue.trim()) {
      setEditingName(false);
      return;
    }
    setEditingName(false);
    if (nameValue.trim() !== extractContactInfo(submission.data).name) {
      setSaving("name");
      try {
        await onUpdate(submission._id, {
          data: { ...submission.data, name: nameValue.trim() },
        });
      } finally {
        setSaving(null);
      }
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleNameSave();
    if (e.key === "Escape") {
      setNameValue(contact?.name || "");
      setEditingName(false);
    }
  };

  const handleNotesBlur = async () => {
    if (!submission) return;
    if (notesValue !== (submission.notes || "")) {
      setSaving("notes");
      try {
        await onUpdate(submission._id, { notes: notesValue });
      } finally {
        setSaving(null);
      }
    }
  };

  const handleAssigneeChange = async (userId: string | null) => {
    if (!submission) return;
    setSaving("assignee");
    try {
      await onUpdate(submission._id, { assignedTo: userId });
    } finally {
      setSaving(null);
    }
  };

  const handleDueDateChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!submission) return;
    const value = e.target.value || null;
    setSaving("dueDate");
    try {
      await onUpdate(submission._id, { dueDate: value });
    } finally {
      setSaving(null);
    }
  };

  const stageColors = currentStage ? getStageColors(currentStage.key) : DEFAULT_STAGE_COLORS;

  const isTerminal =
    submission?.status === "converted" || submission?.stage === "converted" ||
    submission?.status === "rejected" || submission?.stage === "lost";

  return (
    <AnimatePresence>
      {open && submission && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 bg-black/30 z-40"
            onClick={onClose}
          />

          {/* Drawer panel */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 w-full max-w-lg bg-white shadow-2xl z-50 flex flex-col"
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-brand-light/50 to-white flex-shrink-0">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div
                  className="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                  style={{ backgroundColor: ACCENT }}
                >
                  {getInitials(contact?.name || "?")}
                </div>
                <div className="flex-1 min-w-0">
                  {editingName ? (
                    <input
                      ref={nameInputRef}
                      type="text"
                      value={nameValue}
                      onChange={(e) => setNameValue(e.target.value)}
                      onBlur={handleNameSave}
                      onKeyDown={handleNameKeyDown}
                      className="text-lg font-bold text-slate-800 bg-transparent border-b-2 border-emerald-500 outline-none w-full py-0.5"
                    />
                  ) : (
                    <div className="flex items-center gap-2 group/name">
                      <h2 className="text-lg font-bold text-slate-800 truncate">
                        {contact?.name || "Unknown"}
                      </h2>
                      <button
                        onClick={() => setEditingName(true)}
                        className="opacity-0 group-hover/name:opacity-100 transition-opacity p-1 rounded hover:bg-slate-100"
                      >
                        <Edit3 className="h-3.5 w-3.5 text-slate-400" />
                      </button>
                      {saving === "name" && (
                        <Loader2 className="h-3.5 w-3.5 text-emerald-500 animate-spin" />
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-0.5">
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${stageColors.badge}`}
                    >
                      {currentStage?.color && (
                        <span
                          className="h-1.5 w-1.5 rounded-full inline-block"
                          style={{ backgroundColor: currentStage.color }}
                        />
                      )}
                      {currentStage?.label || submission.stage}
                    </span>
                    <span className="text-xs text-slate-400">
                      Added {formatDate(submission.submittedAt, true)}
                    </span>
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-slate-100 transition-colors flex-shrink-0 ml-2"
              >
                <X className="h-5 w-5 text-slate-400" />
              </button>
            </div>

            {/* Drawer body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
              {/* Converted info banner */}
              {submission.convertedToClientId && (
                <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200">
                  <div className="flex items-center gap-2 text-sm text-emerald-700">
                    <CheckCircle2 className="h-4 w-4" />
                    <span className="font-medium">
                      Converted to client: {submission.convertedToClientId.displayName}
                    </span>
                  </div>
                  {submission.convertedToMatterId && (
                    <div className="text-xs text-emerald-600 mt-1 ml-6">
                      Matter: {submission.convertedToMatterId.matterNumber} -{" "}
                      {submission.convertedToMatterId.title}
                    </div>
                  )}
                </div>
              )}

              {/* Assignment + Due Date */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                    Assignee
                    {saving === "assignee" && (
                      <Loader2 className="h-3 w-3 text-emerald-500 animate-spin inline ml-1" />
                    )}
                  </label>
                  <SearchableSelect
                    options={[
                      { value: "", label: "Unassigned" },
                      ...teamUsers.map((u) => ({
                        value: u._id,
                        label: u.name,
                        icon: User,
                      })),
                    ]}
                    value={submission.assignedTo?._id || ""}
                    onChange={(val) => handleAssigneeChange(val || null)}
                    placeholder="Assign to..."
                    icon={User}
                    clearable
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                    Due Date
                    {saving === "dueDate" && (
                      <Loader2 className="h-3 w-3 text-emerald-500 animate-spin inline ml-1" />
                    )}
                  </label>
                  <input
                    type="date"
                    value={submission.dueDate ? submission.dueDate.slice(0, 10) : ""}
                    onChange={handleDueDateChange}
                    className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 bg-white"
                  />
                </div>
              </div>

              {/* Contact info */}
              <div>
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                  Contact Information
                </h3>
                <div className="bg-slate-50 rounded-xl p-4 space-y-2.5">
                  {contact?.email && (
                    <div className="flex items-center gap-3">
                      <Mail className="h-4 w-4 text-slate-400 flex-shrink-0" />
                      <a
                        href={`mailto:${contact.email}`}
                        className="text-sm text-brand hover:underline truncate"
                      >
                        {contact.email}
                      </a>
                    </div>
                  )}
                  {contact?.phone && (
                    <div className="flex items-center gap-3">
                      <Phone className="h-4 w-4 text-slate-400 flex-shrink-0" />
                      <a
                        href={`tel:${contact.phone}`}
                        className="text-sm text-brand hover:underline"
                      >
                        {contact.phone}
                      </a>
                    </div>
                  )}
                  {(submission.data.referral_source || submission.data.source) && (
                    <div className="flex items-center gap-3">
                      <Tag className="h-4 w-4 text-slate-400 flex-shrink-0" />
                      <span className="text-sm text-slate-700">
                        {getSourceLabel(
                          submission.data.referral_source || submission.data.source
                        )}
                      </span>
                    </div>
                  )}
                  {!contact?.email && !contact?.phone && (
                    <p className="text-sm text-slate-400 italic">No contact information</p>
                  )}
                </div>
              </div>

              {/* Notes */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Notes
                  </h3>
                  {saving === "notes" && (
                    <div className="flex items-center gap-1 text-xs text-emerald-600">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Saving...
                    </div>
                  )}
                </div>
                <textarea
                  value={notesValue}
                  onChange={(e) => setNotesValue(e.target.value)}
                  onBlur={handleNotesBlur}
                  rows={3}
                  className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600 resize-none bg-white"
                  placeholder="Add notes about this lead..."
                />
              </div>

              {/* Submission Data */}
              <div>
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                  Submission Data
                </h3>
                <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                  {Object.entries(submission.data).map(([key, value]) => (
                    <div key={key} className="flex items-start gap-3">
                      <span className="text-xs font-medium text-slate-500 w-32 flex-shrink-0 capitalize pt-0.5">
                        {key.replace(/_/g, " ")}:
                      </span>
                      <span className="text-sm text-slate-800 break-words">
                        {typeof value === "object" ? JSON.stringify(value) : String(value || "-")}
                      </span>
                    </div>
                  ))}
                  {Object.keys(submission.data).length === 0 && (
                    <p className="text-sm text-slate-400 italic">No submission data</p>
                  )}
                </div>
              </div>

              {/* Form info */}
              {submission.formId && (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <FileText className="h-4 w-4" />
                  <span>
                    Submitted via:{" "}
                    <span className="font-medium text-slate-700">
                      {submission.formId.name}
                    </span>
                  </span>
                </div>
              )}

              {/* Messages section */}
              <div>
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                  Messages
                </h3>
                {loadingConversations ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-5 w-5 text-slate-300 animate-spin" />
                  </div>
                ) : conversations.length > 0 ? (
                  <div className="space-y-2">
                    {conversations.slice(0, 3).map((conv) => (
                      <div
                        key={conv._id}
                        className="bg-slate-50 rounded-xl p-3 border border-slate-100"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-semibold text-slate-700 truncate">
                            {conv.subject || "Conversation"}
                          </span>
                          {conv.lastMessageAt && (
                            <span className="text-[10px] text-slate-400 flex-shrink-0 ml-2">
                              {formatRelativeTime(conv.lastMessageAt)}
                            </span>
                          )}
                        </div>
                        {conv.lastMessageBody && (
                          <p className="text-xs text-slate-500 line-clamp-2">
                            {conv.lastMessageBody}
                          </p>
                        )}
                      </div>
                    ))}
                    {conversations.length > 3 && (
                      <button className="flex items-center gap-1 text-xs font-medium text-brand hover:text-brand transition-colors">
                        <ExternalLink className="h-3 w-3" />
                        View all {conversations.length} conversations
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="bg-slate-50 rounded-xl p-4 text-center text-sm text-slate-400">
                    <MessageSquare className="h-5 w-5 mx-auto mb-1 text-slate-300" />
                    No messages yet
                  </div>
                )}
              </div>
            </div>

            {/* Drawer footer actions */}
            {!isTerminal && (
              <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex-shrink-0">
                <button
                  onClick={onMarkLost}
                  className="px-4 py-2.5 rounded-xl text-sm font-medium text-red-600 border border-red-200 hover:bg-red-50 transition-colors flex items-center gap-1.5"
                >
                  <XCircle className="h-4 w-4" />
                  Mark as Lost
                </button>
                <button
                  onClick={onConvertClick}
                  className="px-5 py-2.5 rounded-xl text-white text-sm font-bold flex items-center gap-1.5 transition-colors"
                  style={{ backgroundColor: PRIMARY }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = HOVER)}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = PRIMARY)}
                >
                  <ArrowRight className="h-4 w-4" />
                  Convert to Client
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// ─── List View ────────────────────────────────────────────────────────────────

interface ListViewProps {
  submissions: Submission[];
  stages: Stage[];
  onRowClick: (sub: Submission) => void;
}

const ListView: React.FC<ListViewProps> = ({ submissions, stages, onRowClick }) => (
  <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr style={{ backgroundColor: PRIMARY }}>
            <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
              Name
            </th>
            <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
              Contact
            </th>
            <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
              Stage
            </th>
            <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
              Source
            </th>
            <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
              Assigned
            </th>
            <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
              Due Date
            </th>
            <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
              Added
            </th>
          </tr>
        </thead>
        <tbody>
          {submissions.map((sub, i) => {
            const contact = extractContactInfo(sub.data);
            const stage = stages.find((s) => s.key === sub.stage);
            const stageColors = stage ? getStageColors(stage.key) : DEFAULT_STAGE_COLORS;
            const sourceLabel = getSourceLabel(
              sub.data.referral_source || sub.data.source
            );

            return (
              <motion.tr
                key={sub._id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(i * 0.015, 0.3) }}
                onClick={() => onRowClick(sub)}
                className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
              >
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="h-8 w-8 rounded-full flex items-center justify-center text-white font-semibold text-xs flex-shrink-0"
                      style={{ backgroundColor: ACCENT }}
                    >
                      {getInitials(contact.name)}
                    </div>
                    <span className="font-medium text-sm text-slate-800">
                      {contact.name}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-slate-600">
                    {contact.email && <div className="truncate max-w-[200px]">{contact.email}</div>}
                    {contact.phone && (
                      <div className="text-xs text-slate-400">{contact.phone}</div>
                    )}
                    {!contact.email && !contact.phone && (
                      <span className="text-slate-300">-</span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${stageColors.badge}`}
                  >
                    {stage?.color && (
                      <span
                        className="h-1.5 w-1.5 rounded-full inline-block"
                        style={{ backgroundColor: stage.color }}
                      />
                    )}
                    {stage?.label || sub.stage}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-slate-600">
                  {sourceLabel || "-"}
                </td>
                <td className="px-6 py-4 text-sm text-slate-600">
                  {sub.assignedTo?.name || "-"}
                </td>
                <td className="px-6 py-4 text-sm text-slate-500">
                  {sub.dueDate ? formatDate(sub.dueDate) : "-"}
                </td>
                <td className="px-6 py-4 text-sm text-slate-500">
                  {formatDate(sub.submittedAt, true)}
                </td>
              </motion.tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const LeadsPage: React.FC = () => {
  // Data state
  const [stages, setStages] = useState<Stage[]>([]);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [teamUsers, setTeamUsers] = useState<TeamUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [stagesLoading, setStagesLoading] = useState(true);

  // View state
  const [viewMode, setViewMode] = useState<"board" | "list">("board");
  const [searchQuery, setSearchQuery] = useState("");

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<Submission | null>(null);
  const [drawerConversations, setDrawerConversations] = useState<Conversation[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(false);

  // Conversation counts for cards (submissionId -> count)
  const [conversationCounts, setConversationCounts] = useState<Record<string, number>>({});

  // Create Lead modal
  const [showCreateLead, setShowCreateLead] = useState(false);
  const [creatingLead, setCreatingLead] = useState(false);
  const [leadForm, setLeadForm] = useState({
    name: "",
    email: "",
    phone: "",
    notes: "",
    source: "",
    practiceArea: "",
  });

  // Convert modal
  const [showConvertModal, setShowConvertModal] = useState(false);

  // Toasts
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((message: string, type: Toast["type"] = "info") => {
    const id = String(++toastIdCounter);
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // ── Data fetching ───────────────────────────────────────────────────────────

  const fetchStages = useCallback(async () => {
    setStagesLoading(true);
    try {
      const res = await intakeApi.listStages();
      const stagesData: Stage[] = res.data?.data || res.data || [];
      // Sort by order
      stagesData.sort((a, b) => (a.order || 0) - (b.order || 0));
      setStages(stagesData);
    } catch (err) {
      console.error("Failed to fetch stages:", err);
      // Fallback to default stages
      setStages([
        { key: "new", label: "New Lead", color: "#3B82F6", order: 0 },
        { key: "contacted", label: "Contacted", color: "#F59E0B", order: 1 },
        { key: "qualified", label: "Qualified", color: "#8B5CF6", order: 2 },
        { key: "consultation", label: "Consultation", color: "#06B6D4", order: 3 },
        { key: "converted", label: "Converted", color: "#10B981", order: 4 },
        { key: "lost", label: "Lost", color: "#EF4444", order: 5 },
      ]);
    } finally {
      setStagesLoading(false);
    }
  }, []);

  const fetchSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await intakeApi.listSubmissions({ limit: 500 });
      const data = res.data?.data || res.data || [];
      setSubmissions(data);
    } catch (err) {
      console.error("Failed to fetch submissions:", err);
      addToast("Failed to load leads", "error");
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await usersApi.list({ limit: 100 });
      setTeamUsers(res.data?.data || []);
    } catch {
      // Silently fail
    }
  }, []);

  useEffect(() => {
    fetchStages();
    fetchSubmissions();
    fetchUsers();
  }, [fetchStages, fetchSubmissions, fetchUsers]);

  // Fetch conversations when drawer opens
  useEffect(() => {
    if (!drawerOpen || !selectedSubmission) {
      setDrawerConversations([]);
      return;
    }

    const contact = extractContactInfo(selectedSubmission.data);
    if (!contact.email && !contact.phone) {
      setDrawerConversations([]);
      return;
    }

    let cancelled = false;
    setLoadingConversations(true);

    const fetchConvos = async () => {
      try {
        // Try fetching by clientId if converted, otherwise search by email/phone
        const params: Record<string, any> = { limit: 10 };
        if (selectedSubmission.convertedToClientId) {
          params.clientId = selectedSubmission.convertedToClientId._id;
        } else if (contact.email) {
          params.search = contact.email;
        } else if (contact.phone) {
          params.search = contact.phone;
        }
        const res = await conversationsApi.list(params);
        if (!cancelled) {
          setDrawerConversations(res.data?.data || []);
        }
      } catch {
        if (!cancelled) setDrawerConversations([]);
      } finally {
        if (!cancelled) setLoadingConversations(false);
      }
    };

    fetchConvos();
    return () => {
      cancelled = true;
    };
  }, [drawerOpen, selectedSubmission]);

  // ── Group submissions by stage ──────────────────────────────────────────────

  const filteredSubmissions = useMemo(() => {
    if (!searchQuery.trim()) return submissions;
    const q = searchQuery.toLowerCase();
    return submissions.filter((sub) => {
      const contact = extractContactInfo(sub.data);
      return (
        contact.name.toLowerCase().includes(q) ||
        (contact.email && contact.email.toLowerCase().includes(q)) ||
        (contact.phone && contact.phone.includes(q)) ||
        (sub.assignedTo?.name && sub.assignedTo.name.toLowerCase().includes(q))
      );
    });
  }, [submissions, searchQuery]);

  const groupedByStage = useMemo(() => {
    const grouped: Record<string, Submission[]> = {};
    for (const stage of stages) {
      grouped[stage.key] = [];
    }
    for (const sub of filteredSubmissions) {
      const stageKey = sub.stage || "new";
      if (grouped[stageKey]) {
        grouped[stageKey].push(sub);
      } else {
        // If submission has an unknown stage, put it in the first stage
        const firstKey = stages[0]?.key;
        if (firstKey && grouped[firstKey]) {
          grouped[firstKey].push(sub);
        }
      }
    }
    return grouped;
  }, [filteredSubmissions, stages]);

  // ── Actions ─────────────────────────────────────────────────────────────────

  const handleDragEnd = useCallback(
    async (result: DropResult) => {
      const { destination, source, draggableId } = result;

      // Dropped outside any droppable
      if (!destination) return;

      // Dropped in same position
      if (
        destination.droppableId === source.droppableId &&
        destination.index === source.index
      ) {
        return;
      }

      const submissionId = draggableId;
      const destStage = destination.droppableId;

      // If dragged to "converted" column, open the wizard instead of silently converting
      if (destStage === "converted") {
        const sub = submissions.find((s) => s._id === submissionId);
        if (sub && sub.status !== "converted") {
          openConvertModal(sub);
          return;
        }
      }

      // Build a snapshot of state before the optimistic update
      const prevSubmissions = [...submissions];

      // Optimistically update local state
      setSubmissions((prev) =>
        prev.map((sub) => {
          if (sub._id === submissionId) {
            const newStatus =
              destStage === "lost"
                ? "rejected"
                : sub.status === "converted" || sub.status === "rejected"
                ? "new"
                : sub.status;
            return { ...sub, stage: destStage, status: newStatus };
          }
          return sub;
        })
      );

      // Also update selected submission if it's the same one
      if (selectedSubmission?._id === submissionId) {
        const newStatus =
          destStage === "lost"
            ? "rejected"
            : selectedSubmission.status === "converted" ||
              selectedSubmission.status === "rejected"
            ? "new"
            : selectedSubmission.status;
        setSelectedSubmission((prev) =>
          prev ? { ...prev, stage: destStage, status: newStatus } : prev
        );
      }

      // Call API
      try {
        const updatePayload: Record<string, any> = { stage: destStage };
        if (destStage === "lost") updatePayload.status = "rejected";

        await intakeApi.updateSubmission(submissionId, updatePayload);
        addToast(
          `Moved to ${stages.find((s) => s.key === destStage)?.label || destStage}`,
          "success"
        );
      } catch (err) {
        console.error("Failed to update stage:", err);
        // Revert
        setSubmissions(prevSubmissions);
        if (selectedSubmission?._id === submissionId) {
          const original = prevSubmissions.find((s) => s._id === submissionId);
          if (original) setSelectedSubmission(original);
        }
        addToast("Failed to move lead. Please try again.", "error");
      }
    },
    [submissions, selectedSubmission, stages, addToast]
  );

  const handleUpdateSubmission = useCallback(
    async (id: string, data: Record<string, any>) => {
      try {
        await intakeApi.updateSubmission(id, data);
        // Refresh from API to get the latest
        const res = await intakeApi.getSubmission(id);
        const updated: Submission = res.data?.data || res.data;
        setSubmissions((prev) =>
          prev.map((sub) => (sub._id === id ? updated : sub))
        );
        if (selectedSubmission?._id === id) {
          setSelectedSubmission(updated);
        }
      } catch (err) {
        console.error("Failed to update submission:", err);
        addToast("Failed to save changes", "error");
        throw err;
      }
    },
    [selectedSubmission, addToast]
  );

  const handleCreateLead = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadForm.name.trim()) return;
    setCreatingLead(true);
    try {
      const res = await intakeApi.createSubmission({
        source: "manual",
        data: {
          name: leadForm.name.trim(),
          email: leadForm.email || undefined,
          phone: leadForm.phone || undefined,
          notes: leadForm.notes || undefined,
          referral_source: leadForm.source || undefined,
          practice_area: leadForm.practiceArea || undefined,
        },
      });

      // Optimistically add to local state
      const newSub: Submission = res.data?.data || res.data;
      if (newSub?._id) {
        setSubmissions((prev) => [newSub, ...prev]);
      }

      setShowCreateLead(false);
      setLeadForm({ name: "", email: "", phone: "", notes: "", source: "", practiceArea: "" });
      addToast("Lead created successfully", "success");

      // Refresh in background to ensure consistency
      fetchSubmissions();
    } catch (err) {
      console.error("Failed to create lead:", err);
      addToast("Failed to create lead", "error");
    } finally {
      setCreatingLead(false);
    }
  };

  const openConvertModal = (sub: Submission) => {
    setSelectedSubmission(sub);
    setShowConvertModal(true);
  };


  const handleMarkLost = useCallback(async () => {
    if (!selectedSubmission) return;
    const lostStage = stages.find(
      (s) => s.key === "lost" || s.key === "rejected"
    );
    const stageKey = lostStage?.key || "lost";
    const prevSubmissions = [...submissions];

    // Optimistic update
    setSubmissions((prev) =>
      prev.map((sub) =>
        sub._id === selectedSubmission._id
          ? { ...sub, stage: stageKey, status: "rejected" }
          : sub
      )
    );
    setSelectedSubmission((prev) =>
      prev ? { ...prev, stage: stageKey, status: "rejected" } : prev
    );
    setDrawerOpen(false);

    try {
      await intakeApi.updateSubmission(selectedSubmission._id, {
        stage: stageKey,
        status: "rejected",
      });
      addToast("Lead marked as lost", "info");
    } catch (err) {
      console.error("Failed to mark as lost:", err);
      setSubmissions(prevSubmissions);
      addToast("Failed to update lead", "error");
    }
  }, [selectedSubmission, stages, submissions, addToast]);

  const openDrawer = useCallback((sub: Submission) => {
    setSelectedSubmission(sub);
    setDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
  }, []);

  // ── Computed values ─────────────────────────────────────────────────────────

  const totalCount = submissions.length;
  const isLoading = loading || stagesLoading;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5 flex-shrink-0">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Lead Pipeline</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {totalCount} lead{totalCount !== 1 ? "s" : ""} across{" "}
            {stages.length} stages
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search leads..."
              className="pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600 w-56 bg-white"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-slate-100"
              >
                <X className="h-3.5 w-3.5 text-slate-400" />
              </button>
            )}
          </div>

          {/* Add Lead button */}
          <button
            onClick={() => setShowCreateLead(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-white font-bold rounded-xl transition-colors"
            style={{ backgroundColor: PRIMARY }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.backgroundColor = HOVER)
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.backgroundColor = PRIMARY)
            }
          >
            <Plus className="h-4 w-4" />
            Add Lead
          </button>

          {/* View Toggle */}
          <div className="flex rounded-xl border border-slate-200 overflow-hidden">
            <button
              onClick={() => setViewMode("board")}
              className={`flex items-center gap-1.5 px-3.5 py-2.5 text-sm font-medium transition-colors ${
                viewMode === "board"
                  ? "text-white"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
              style={viewMode === "board" ? { backgroundColor: PRIMARY } : {}}
              title="Board view"
            >
              <LayoutGrid className="h-4 w-4" />
              Board
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`flex items-center gap-1.5 px-3.5 py-2.5 text-sm font-medium transition-colors ${
                viewMode === "list"
                  ? "text-white"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
              style={viewMode === "list" ? { backgroundColor: PRIMARY } : {}}
              title="List view"
            >
              <List className="h-4 w-4" />
              List
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20 flex-1">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto" />
            <p className="text-sm text-slate-400 mt-3">Loading pipeline...</p>
          </div>
        </div>
      ) : viewMode === "board" ? (
        /* ── Board View ─────────────────────────────────────────────────── */
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="flex gap-4 overflow-x-auto pb-4 flex-1 min-h-0">
            {stages.map((stage, idx) => (
              <BoardColumn
                key={stage.key}
                stage={stage}
                submissions={groupedByStage[stage.key] || []}
                conversationCounts={conversationCounts}
                onCardClick={openDrawer}
                onAddLead={() => setShowCreateLead(true)}
                isFirstColumn={idx === 0}
              />
            ))}
          </div>
        </DragDropContext>
      ) : (
        /* ── List View ──────────────────────────────────────────────────── */
        <div className="flex-1 overflow-y-auto">
          {filteredSubmissions.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col items-center justify-center py-20"
            >
              {searchQuery ? (
                <>
                  <div className="relative mb-6">
                    <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-slate-100 to-slate-50 blur-xl scale-150 opacity-60" />
                    <div className="relative p-5 rounded-3xl bg-gradient-to-br from-slate-50 to-white border border-slate-100 shadow-sm">
                      <Search className="h-10 w-10 text-slate-400" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2">No leads match your search</h3>
                  <p className="text-sm text-slate-500 text-center max-w-md leading-relaxed">
                    Try adjusting your search terms or clear the filter to see all leads.
                  </p>
                </>
              ) : (
                <>
                  <div className="relative mb-6">
                    <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-60" />
                    <div className="relative p-5 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
                      <UserPlus className="h-10 w-10" style={{ color: "var(--color-primary)" }} />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-slate-800 mb-2">No leads yet</h3>
                  <p className="text-sm text-slate-500 text-center max-w-md mb-6 leading-relaxed">
                    Start building your pipeline by adding your first lead. Track prospects from initial contact through to client conversion.
                  </p>
                  <button
                    onClick={() => setShowCreateLead(true)}
                    className="inline-flex items-center gap-2 px-5 py-3 text-white font-bold rounded-xl transition-colors"
                    style={{ backgroundColor: PRIMARY }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = HOVER)}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = PRIMARY)}
                  >
                    <Plus className="h-4 w-4" />
                    Add Lead
                  </button>
                </>
              )}
            </motion.div>
          ) : (
            <ListView
              submissions={filteredSubmissions}
              stages={stages}
              onRowClick={openDrawer}
            />
          )}
        </div>
      )}

      {/* Detail Drawer */}
      <DetailDrawer
        open={drawerOpen}
        submission={selectedSubmission}
        stages={stages}
        teamUsers={teamUsers}
        onClose={closeDrawer}
        onUpdate={handleUpdateSubmission}
        onConvertClick={() => {
          if (selectedSubmission) openConvertModal(selectedSubmission);
        }}
        onMarkLost={handleMarkLost}
        conversations={drawerConversations}
        loadingConversations={loadingConversations}
      />

      {/* Create Lead Modal */}
      <PremiumModal
        open={showCreateLead}
        onClose={() => setShowCreateLead(false)}
        title="Add Lead"
        subtitle="Manually add a new lead"
        icon={UserPlus}
        footer={
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowCreateLead(false)}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="create-lead-form"
              disabled={creatingLead || !leadForm.name.trim()}
              className="px-5 py-2.5 rounded-xl text-white text-sm font-bold transition-colors disabled:opacity-50"
              style={{ backgroundColor: PRIMARY }}
            >
              {creatingLead ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating...
                </span>
              ) : (
                "Add Lead"
              )}
            </button>
          </div>
        }
      >
        <form
          id="create-lead-form"
          onSubmit={handleCreateLead}
          className="space-y-5"
        >
          {/* Contact Section */}
          <div className="flex items-center gap-3 pt-2">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Contact</span>
            <div className="flex-1 h-px bg-slate-100" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Name <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                required
                value={leadForm.name}
                onChange={(e) =>
                  setLeadForm({ ...leadForm, name: e.target.value })
                }
                className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                placeholder="Full name"
                autoFocus
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="email"
                  value={leadForm.email}
                  onChange={(e) =>
                    setLeadForm({ ...leadForm, email: e.target.value })
                  }
                  className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                  placeholder="email@example.com"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Phone
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="tel"
                  value={leadForm.phone}
                  onChange={(e) =>
                    setLeadForm({ ...leadForm, phone: e.target.value })
                  }
                  className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
          </div>

          {/* Details Section */}
          <div className="flex items-center gap-3 pt-2">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Details</span>
            <div className="flex-1 h-px bg-slate-100" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Referral Source
            </label>
            <div className="flex flex-wrap gap-2">
              {REFERRAL_SOURCES.map((src) => (
                <button
                  key={src.value}
                  type="button"
                  onClick={() =>
                    setLeadForm({
                      ...leadForm,
                      source: leadForm.source === src.value ? "" : src.value,
                    })
                  }
                  className={
                    leadForm.source === src.value
                      ? "px-3 py-1.5 rounded-full border border-brand-accent bg-brand-light text-sm text-brand font-medium transition-all cursor-pointer"
                      : "px-3 py-1.5 rounded-full border border-slate-200 text-sm text-slate-600 hover:border-slate-300 hover:bg-slate-50 transition-all cursor-pointer"
                  }
                >
                  {src.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Area of Interest
            </label>
            <div className="flex flex-wrap gap-2">
              {PRACTICE_AREAS.map((area) => (
                <button
                  key={area.value}
                  type="button"
                  onClick={() =>
                    setLeadForm({
                      ...leadForm,
                      practiceArea:
                        leadForm.practiceArea === area.value ? "" : area.value,
                    })
                  }
                  className={
                    leadForm.practiceArea === area.value
                      ? "px-3 py-1.5 rounded-full border border-brand-accent bg-brand-light text-sm text-brand font-medium transition-all cursor-pointer"
                      : "px-3 py-1.5 rounded-full border border-slate-200 text-sm text-slate-600 hover:border-slate-300 hover:bg-slate-50 transition-all cursor-pointer"
                  }
                >
                  {area.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Notes
            </label>
            <div className="relative">
              <MessageSquare className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <textarea
                value={leadForm.notes}
                onChange={(e) =>
                  setLeadForm({ ...leadForm, notes: e.target.value })
                }
                rows={4}
                className="w-full rounded-xl border border-slate-200 pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-colors resize-none"
                placeholder="Brief description of their legal needs..."
              />
            </div>
          </div>
        </form>
      </PremiumModal>

      {/* Convert Lead Wizard */}
      <ConvertLeadWizard
        open={showConvertModal}
        onClose={() => setShowConvertModal(false)}
        submission={selectedSubmission}
        onConverted={() => {
          setShowConvertModal(false);
          setDrawerOpen(false);
          addToast("Lead converted to client!", "success");
          fetchSubmissions();
        }}
      />

      {/* Toast notifications */}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
};

export default LeadsPage;
